import React, { useState, useEffect } from 'react';
import { ENDPOINTS } from '../../config/constants';
import { trackPurchase } from '../../utils/metaPixel';

interface PhoneVerificationProps {
  orderId: number;
  phoneNumber: string;
  onVerified: () => void;
  onCancel: () => void;
}

export const PhoneVerification: React.FC<PhoneVerificationProps> = ({
  orderId,
  phoneNumber,
  onVerified,
  onCancel
}) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [countdown, setCountdown] = useState(300); // Start with 5 minutes
  const [isVerified, setIsVerified] = useState(false);

  // Start countdown on mount
  useEffect(() => {
    let mounted = true;
    let statusTimer: NodeJS.Timeout;

    const checkStatus = async () => {
      if (!mounted) return;

      try {
        const response = await fetch(ENDPOINTS.SMS_VERIFICATION.STATUS(orderId.toString()), {
          method: 'GET'
        });
        
        if (!mounted) return;

        const data = await response.json();
        
        // Handle different verification statuses
        switch (data.status) {
          case 'VERIFIED':
            setIsVerified(true);
            onVerified();
            break;
          case 'PENDING':
            // Code is active and pending verification
            setCountdown(300);
            // Poll status every 5 seconds while pending
            statusTimer = setTimeout(checkStatus, 5000);
            break;
          case 'EXPIRED':
            // Code has expired - allow requesting new code
            setCountdown(0);
            setError('Codul a expirat. Vă rugăm să solicitați un cod nou.');
            break;
          case 'FAILED':
            setError('Prea multe încercări eșuate. Vă rugăm să solicitați un cod nou.');
            setCountdown(0);
            break;
          default:
            if (data.message === 'Cod de verificare activ') {
              setCountdown(300);
              // Poll status every 5 seconds
              statusTimer = setTimeout(checkStatus, 5000);
            }
        }
      } catch (err) {
        if (!mounted) return;
        console.error('Status check failed:', err);
        setError('Eroare la verificarea statusului. Încercați din nou.');
      }
    };

    if (!isVerified) {
      checkStatus();
    }

    // Cleanup function
    return () => {
      mounted = false;
      if (statusTimer) {
        clearTimeout(statusTimer);
      }
    };
  }, [orderId, isVerified, onVerified]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const sendVerificationCode = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch(ENDPOINTS.SMS_VERIFICATION.SEND(orderId.toString()), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phoneNumber })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Eroare la trimiterea codului');
      }

      // Start countdown for resend button (5 minutes)
      setCountdown(300);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Eroare la trimiterea codului');
    } finally {
      setIsLoading(false);
    }
  };

  const verifyCode = async () => {
    if (!code) {
      setError('Vă rugăm să introduceți codul');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Check verification status first
      const statusResponse = await fetch(ENDPOINTS.SMS_VERIFICATION.STATUS(orderId.toString()), {
        method: 'GET'
      });
      const statusData = await statusResponse.json();

      // If already verified, proceed
      if (statusData.status === 'VERIFIED') {
        setIsVerified(true);
        onVerified();
        return;
      }

      // Otherwise verify the code
      const response = await fetch(ENDPOINTS.SMS_VERIFICATION.VERIFY(orderId.toString()), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code })
      });

      const data = await response.json();
      if (!response.ok) {
        const errorMessage = (() => {
          switch (data.error) {
            case 400:
              setCode(''); // Clear invalid code
              return 'Cod invalid. Vă rugăm să verificați și să încercați din nou.';
            case 401:
              setCode(''); // Clear expired code
              return 'Codul a expirat. Vă rugăm să solicitați un cod nou.';
            case 404:
              return 'Codul de verificare nu a fost găsit';
            case 429:
              return 'Prea multe încercări. Vă rugăm să așteptați câteva minute.';
            default:
              setCode(''); // Clear code on unknown errors
              return data.message || 'Eroare la verificarea codului';
          }
        })();
        throw new Error(errorMessage);
      }

      // Show success message before proceeding
      setError(null);
      setIsVerified(true);

      // Track purchase event for RAMBURS orders
      trackPurchase({
        value: 60,
        orderId: orderId.toString()
      });

      // Verification successful - AWB generation is handled by the backend
      onVerified();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Eroare la verificarea codului');
      setCode(''); // Clear invalid code
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-gray-900 p-6 rounded-lg shadow-lg max-w-md mx-auto relative">
      {/* Close button */}
      <button
        onClick={onCancel}
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-300 
          focus:outline-none transition-colors duration-200"
      >
        <span className="sr-only">Închide</span>
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      <h3 className="text-xl font-semibold text-red-500 mb-4">
         Comandă în curs de confirmare!
      </h3>
      
      <p className="text-gray-400 mb-6">
        BlackFamily a trimis un cod de verificare prin SMS la numărul {phoneNumber}.
        Vă rugăm să introduceți codul pentru a continua.
      </p>

      <div className="space-y-4">
        <div>
          <label htmlFor="verificationCode" className="block text-sm font-medium text-gray-300 mb-1">
            Cod de verificare
          </label>
          <input
            type="text"
            id="verificationCode"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-md text-gray-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            placeholder="Introduceți codul din 6 cifre"
            maxLength={6}
            disabled={isLoading}
          />
        </div>

        {error && (
          <p className="text-red-500 text-sm">{error}</p>
        )}

        <div className="flex justify-between items-center">
          <button
            type="button"
            onClick={sendVerificationCode}
            disabled={isLoading || countdown > 0}
            className="text-sm text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {countdown > 0 ? (
              `Retrimite cod (${Math.floor(countdown / 60)}:${(countdown % 60).toString().padStart(2, '0')})`
            ) : (
              'Retrimite cod'
            )}
          </button>

          <div className="space-x-3">
            <button
              type="button"
              onClick={onCancel}
              disabled={isLoading}
              className="px-4 py-2 text-sm text-gray-400 hover:text-white border border-gray-700 rounded-md hover:border-gray-600 focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              Anulează
            </button>

            <button
              type="button"
              onClick={verifyCode}
              disabled={isLoading || code.length !== 6}
              className={`px-4 py-2 text-sm rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed
                ${isVerified 
                  ? 'bg-green-600 hover:bg-green-700 text-white' 
                  : 'bg-red-600 hover:bg-red-700 text-white'}`}
            >
              {isLoading ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Se verifică...
                </span>
              ) : isVerified ? (
                <span className="flex items-center">
                  <svg className="-ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Verificat
                </span>
              ) : (
                'Verifică'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
